import * as React from "react";
import { useForm } from "react-hook-form";
import { trackEvent } from "contexts/mixpanel";
import type { CommunityVotePostBody, OpenCallVoteStep } from "../types";
import { useSubmissionsForCuration } from "./useSubmissionsForCuration";
import { usePartnerCuration } from "./usePartnerCuration";

/**
 * Form and mutation hooks for Community Voting on Open Call Submissions
 */
const useOpenCallCurationForm = (openCallId = "", curatorId = "") => {
  // State to track current step in voting process, affects copy and visible submissions
  const [step, setStep] = React.useState<OpenCallVoteStep>("selecting");

  React.useEffect(() => {
    document
      .querySelector("#community-vote-heading")
      ?.scrollIntoView({ behavior: "smooth" });
  }, [step]);

  // Form state with field shape that matches the mutation request body
  const { handleSubmit, ...form } = useForm<CommunityVotePostBody>({
    defaultValues: {
      openCallId,
      submissionIds: [],
    },
  });

  // Watch this field for changes to track number vs intended number of selections
  const selections = form.watch("submissionIds");
  // Fetch all submissions to curate on
  const { data: submissionResp, ...query } = useSubmissionsForCuration(
    openCallId,
    curatorId,
  );

  // Create POST Partner Curation mutation
  const { mutate, ...mutation } = usePartnerCuration();

  // Determine visible submissions in grid based on current `step`
  const submissions = submissionResp?.data ?? [];
  let visibleSubmissions = submissions;
  if (step !== "selecting") {
    visibleSubmissions = submissions.filter((s) =>
      selections.includes(s.submissionId),
    );
  }

  // Create submit handler that increments `step` on success
  const onSubmit = handleSubmit((formData) =>
    mutate(
      {
        ...formData,
        magicLink: curatorId,
      },
      {
        onSuccess: () => {
          setStep("done");
          trackEvent({
            name: "Form Submit",
            FormName: "Open Call Partner Curation",
            OpenCallId: openCallId,
          });
        },
      },
    ),
  );

  return {
    /** Form state */
    ...form,
    /** POST Partner Curation mutation state */
    mutation,
    /** Query state for submissions, randomized for voting */
    query,
    /** Form submit handler */
    onSubmit,
    /** Current step in voting process */
    step,
    /** Setter for current step in voting process */
    setStep,
    /** Array of selected submission ids (i.e. `submission.submissionId`) */
    selections,
    /** Array of all submissions for this open call */
    submissions,
    /** Array of all submissions OR selected submissions, depending on `step` */
    visibleSubmissions,
  };
};

export { useOpenCallCurationForm };
